// useWorkout — the write path for Mirror-driven workout voice-capture.
//
// Parity with useFocus.saveFocusSession / useSleep.saveSleepLog: a single,
// focused writer the confirm modal (WorkoutLogModal) calls. It inserts into the
// SAME table + columns the manual flows write (FitnessWorkout.tsx:322,
// WorkoutSession.tsx:112) — name (NOT NULL), exercises (jsonb default '[]'),
// duration_minutes (nullable), source — so the voice path doesn't fork a second
// row shape. NOTE: the manual workout inserts are inline in those page
// components (no pre-existing shared saveWorkout hook to import), so this hook is
// the first reusable writer; it deliberately mirrors their column set rather than
// inventing a new one.
//
// Idempotency: source + client_action_id ("mirror_msg_<action_id>") dedupe a
// double-confirm via the partial unique index on (user_id, client_action_id)
// (migration add_client_action_id_to_workout_sessions). A 23505 replay returns
// the existing row WITHOUT re-awarding XP — exactly like useFocus.
//
// XP: base XP for a completed workout is awarded HERE (hoist, #245) so voice and
// the manual finish (FitnessWorkout.tsx:558 addXP("fitness","workout_completed",
// 50)) earn identically. Only fires on a real insert, never on an idempotent
// replay.

import { useCallback, useEffect, useRef } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useXP } from "@/hooks/useXP";

export interface WorkoutSessionRow {
  id: string;
  user_id: string;
  name: string;
  exercises: unknown[];
  duration_minutes: number | null;
  source: string;
  started_at: string | null;
  completed_at: string | null;
}

// Base XP for finishing a workout — must match the manual finish award
// (FitnessWorkout.tsx:558) so voice-capture and the in-app workout earn the same.
const WORKOUT_BASE_XP = 50;

export function useWorkout() {
  const { addXP } = useXP({ guarded: true });
  const mounted = useRef(false), epoch = useRef(0), actor = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    mounted.current = true;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const next = session?.user.id ?? null;
      if (actor.current !== undefined && next !== actor.current) epoch.current++;
      actor.current = next;
    });
    const lifecycle = epoch;
    return () => { mounted.current = false; lifecycle.current++; subscription.unsubscribe(); };
  }, []);

  const saveWorkoutSession = useCallback(async (
    name: string,
    durationMinutes: number | null,
    opts?: { source?: string; clientActionId?: string; ownerId?: string; isCurrent?: () => boolean },
  ): Promise<WorkoutSessionRow | null> => {
    const run = epoch.current;
    const current = () => mounted.current && run === epoch.current && (opts?.isCurrent?.() ?? true);
    if (!current()) return null;
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) {
        console.error("[useWorkout] saveWorkoutSession auth error:", authError);
        return null;
      }
      if (!user || !current() || opts?.ownerId && user.id !== opts.ownerId) return null;
      if (actor.current === undefined) actor.current = user.id;
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (!current() || sessionError || session?.user.id !== user.id || !session.access_token) return null;
      const matchesReview = (value: Record<string, unknown> | null) => !!value && typeof value.id === "string" && !!value.id
        && value.user_id === user.id && value.name === name && value.duration_minutes === durationMinutes
        && Array.isArray(value.exercises) && value.exercises.length === 0
        && (!opts?.source || value.source === opts.source)
        && (!opts?.clientActionId || value.client_action_id === opts.clientActionId);

      const nowIso = new Date().toISOString();
      // Same column set the manual flows write. exercises stays [] (the DB
      // default) — v1 voice capture is name + duration; detailed {name,sets}
      // exercises come from the manual workout flow. completed_at marks it done.
      const row: Record<string, unknown> = {
        user_id: user.id,
        name,
        exercises: [],
        duration_minutes: durationMinutes,
        started_at: nowIso,
        completed_at: nowIso,
      };
      if (opts?.source) row.source = opts.source;
      if (opts?.clientActionId) row.client_action_id = opts.clientActionId;

      const sb = supabase as unknown as SupabaseClient;
      const { data, error } = await sb
        .from("workout_sessions")
        .insert(row)
        .select()
        .single()
        .setHeader("Authorization", "Bearer " + session.access_token);
      if (!current()) return null;

      if (error) {
        // Idempotent replay — a duplicate client_action_id (voice double-confirm)
        // hits the partial unique index (23505). Return the existing row so the
        // caller treats it as success, WITHOUT re-awarding XP.
        if ((error as { code?: string }).code === "23505" && opts?.clientActionId) {
          const { data: existing } = await sb
            .from("workout_sessions")
            .select("*")
            .eq("user_id", user.id)
            .eq("client_action_id", opts.clientActionId)
            .maybeSingle()
            .setHeader("Authorization", "Bearer " + session.access_token);
          if (!current()) return null;
          if (matchesReview(existing)) {
            console.info("[useWorkout] idempotent replay — returning existing session");
            return existing as WorkoutSessionRow;
          }
        }
        console.error("[useWorkout] workout_sessions insert failed:", error, "payload:", row);
        return null;
      }
      if (!matchesReview(data)) {
        console.error("[useWorkout] workout_sessions save not confirmed");
        return null;
      }

      const saved = data as WorkoutSessionRow;
      // Base XP — awarded here so voice-capture and the manual finish earn
      // identically. Only on a real insert (the 23505 replay path returned above).
      try { await addXP("fitness", "workout_completed", WORKOUT_BASE_XP, saved.id, { ownerId: user.id, isCurrent: current }); } catch { /* the workout is already confirmed */ }
      return current() ? saved : null;
    } catch (e) {
      console.error("[useWorkout] Save exception:", e);
      return null;
    }
  }, [addXP]);

  return { saveWorkoutSession };
}
